<template>
  <div
    v-if="entranceOptions"
    class="row align-items-center"
  >
    <label class="col-sm-3 control-label"> Venue Entrance or Side </label>
    <div class="col-sm-8">
      <dropdown-list
        v-model="entranceOptionKey"
        :options="entranceOptions"
        required
        :disabled="disabled"
        @change="onChooseEntrance"
      />
    </div>
  </div>

  <div class="row align-items-center">
    <label class="col-sm-3 control-label"> Specific Entrance or Side</label>
    <div class="col-sm-8">
      <sanitized-input
        v-model="entrance"
        placeholder=""
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';

import { Venue, VenueSelection } from '@/models/venue';
import type { CustomLocationType } from '@/models/address';
import {
  ORIGINAL_ADDRESS_ITEM,
  buildEntranceOptions,
  getEntranceKeyFromAddress,
  getZoneLocationByEntrance,
} from './venueInfo';

const props = defineProps<{
  venues: Venue[] | null;
  address: CustomLocationType;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (event: 'selected', selection: VenueSelection): void;
}>();

const entrance = shallowRef('');
const entranceOptionKey = shallowRef(ORIGINAL_ADDRESS_ITEM.key);

const entranceOptions = computed(() => buildEntranceOptions(props.venues));

const onChooseEntrance = () => {
  const { latitude, longitude, originalCoordinates } = props.address;

  if (entranceOptionKey.value === ORIGINAL_ADDRESS_ITEM.key) {
    const lat = originalCoordinates?.lat ?? latitude;
    const lng = originalCoordinates?.lng ?? longitude;

    if (!lat || !lng) {
      return;
    }

    emit('selected', {
      name: '',
      position: { lat: Number(lat), lng: Number(lng) },
    });

    return;
  }

  const info = getZoneLocationByEntrance(entranceOptionKey.value, props.venues, entranceOptions.value);
  if (!info) {
    return;
  }

  const lat = info.location?.latitude ?? info.zone?.latitude;
  const lng = info.location?.longitude ?? info.zone?.longitude;

  if (!lat || !lng) {
    return;
  }

  emit('selected', {
    ...info,
    position: {
      lat,
      lng,
    },
  });
};

const getEntranceByAddress = () => {
  const key = getEntranceKeyFromAddress(props.address);
  const selectedEntrance = entranceOptions.value?.find(item => item.key === key);
  entranceOptionKey.value = selectedEntrance?.key ?? ORIGINAL_ADDRESS_ITEM.key;
  entrance.value = props.address.entranceName ?? '';
};

watch(() => props.venues, getEntranceByAddress);

watch(entrance, (val) => {
  const info = getZoneLocationByEntrance(entranceOptionKey.value, props.venues, entranceOptions.value);

  emit('selected', {
    venue: info?.venue,
    zone: info?.zone,
    location: info?.location,
    name: val,
    position: {
      lat: Number(props.address.latitude),
      lng: Number(props.address.longitude),
    },
  });
});

watch(() => props.address, getEntranceByAddress, {
  deep: true,
});
</script>
